import type { Catalogue } from '@daghfal/core';

/**
 * The platform console's own messages, merged over `commonStrings`.
 *
 * This surface is read by Nasca-Sy employees rather than by families, so its register is operational —
 * "tenant", "plan", "suspend" — where the tenant-facing apps say "nursery" and "subscription".
 */
export const strings: Catalogue = {
  ar: {
    'console.name': 'وحدة تحكّم المنصّة',
    'console.tagline': 'إدارة المشتركين — Nasca-Sy',

    'nav.dashboard': 'اللوحة',
    'nav.tenants': 'المشتركون',
    'nav.users': 'مستخدمو المنصّة',
    'nav.accounts': 'الحسابات',

    'accounts.title': 'حسابات تحتاج قراراً',
    'accounts.lede': 'من لا تستطيع حضانة واحدة أن تقرّر في حسابه: من في أكثر من حضانة أو في لا شيء، ومن يدير حضانة، وفريق المنصّة نفسه.',
    'accounts.queue.title': 'طلبات إعادة تعيين كلمة السر',
    'accounts.queue.hint': 'تأكّد من الشخص قبل الموافقة، بالتواصل مع حضانته أو بوسيلة تثق بها.',
    'accounts.queue.empty': 'لا طلبات تنتظر.',
    'accounts.queue.approve': 'تأكدتُ منه',
    'accounts.queue.reject': 'رفض',
    'accounts.name': 'الاسم',
    'accounts.phone': 'رقم الهاتف',
    'accounts.requestedAt': 'تاريخ الطلب',
    'accounts.from': 'من أين',
    'accounts.find.title': 'البحث عن حساب برقمه',
    'accounts.find.submit': 'ابحث',
    'accounts.find.none': 'لا حساب لهذا الرقم.',
    'accounts.platform': 'المنصّة',
    'accounts.lastSeen': 'آخر استخدام: {date}',
    'accounts.status.Active': 'فعّال',
    'accounts.status.Suspended': 'موقوف',
    'accounts.status.Deactivated': 'معطّل',
    'accounts.status.Released': 'فُصل رقمه',
    'accounts.tier.None': 'لا صفة فعّالة',
    'accounts.tier.Guardian': 'وليّ أمر',
    'accounts.tier.Staff': 'موظف حضانة',
    'accounts.tier.CentreAdmin': 'إدارة حضانة',
    'accounts.tier.Platform': 'فريق المنصّة',
    'accounts.renumber.title': 'نقل الحساب إلى رقم جديد',
    'accounts.renumber.phone': 'الرقم الجديد',
    'accounts.renumber.submit': 'أرسل الرابط',
    'accounts.renumber.sent': 'أُرسل الرابط إلى {phone}، وينتهي في {date}.',
    'accounts.link.title': 'رابط استعادة لمرة واحدة',
    'accounts.link.hint': 'يضع به صاحب الحساب كلمة سر جديدة بنفسه. لا يُرسل برسالة نصية: سلّمه بوسيلة تثق بها.',
    'accounts.link.submit': 'أنشئ الرابط',
    'accounts.link.shown': 'يظهر الرابط هنا مرة واحدة فقط، وينتهي في {date}. انسخه الآن.',
    'accounts.release.title': 'فصل الرقم عن الحساب',
    'accounts.release.hint': 'حين يصير الرقم لشخص آخر. يبقى الحساب باسمه وسجلاته، ولا يستطيع أحد الدخول إليه بعد اليوم.',
    'accounts.release.confirm': 'تأكدتُ أن الرقم لم يعد لصاحب الحساب',
    'accounts.release.submit': 'افصل الرقم',
    'accounts.release.done': 'فُصل الرقم عن الحساب.',

    'passkeyRequired.title': 'أضف مفتاح دخول',
    'passkeyRequired.body': 'لا تُفتح لوحة المنصّة إلا بجلسة بدأت بمفتاح دخول. أضف مفتاحاً على هذا الجهاز، ثم سجّل الخروج وادخل به.',
    'passkeyRequired.signOut': 'سجّل الخروج لأدخل بمفتاح الدخول',
    'nav.announcements': 'الإعلانات',
    'nav.audit': 'سجل التدقيق',
    'nav.backups': 'النسخ الاحتياطي',
    'nav.allergens': 'مسبّبات الحساسية',
    'allergens.title': 'مسبّبات الحساسية',
    'allergens.lede': 'القائمة التي تُعرف بها الحساسية في كل الحضانات، ورمز كل مادة. يبقى اسم الحساسية كما كتبته الحضانة، ويُطابَق بهذه القائمة كلما عُرض: فكل اسم يُضاف هنا يُظهر رمز المادة فورًا عند كل طفل كُتبت حساسيته به، في كل الحضانات.',
    'allergens.new': 'مادة جديدة',
    'allergens.catalogue': 'القائمة',
    'allergens.search': 'ابحث بأي اسم',
    'allergens.column.icon': 'الرمز',
    'allergens.column.nameAr': 'الاسم بالعربية',
    'allergens.column.nameEn': 'الاسم بالإنكليزية',
    'allergens.column.kind': 'النوع',
    'allergens.column.names': 'الأسماء',
    'allergens.column.state': 'الحالة',
    'allergens.kind.Food': 'طعام',
    'allergens.kind.Condition': 'حالة صحية',
    'allergens.kind.Medicine': 'دواء',
    'allergens.kind.Environment': 'بيئة',
    'allergens.state.active': 'في القائمة',
    'allergens.state.retired': 'خارج القائمة',
    'allergens.state.merged': 'مدموجة',
    'allergens.edit': 'تعديل',
    'allergens.close': 'إغلاق',
    'allergens.editor.new': 'مادة جديدة',
    'allergens.editor.edit': 'تعديل «{name}»',
    'allergens.merged.notice': 'دُمجت هذه المادة في مادة أخرى ونُقلت إليها أسماؤها، فلا تُعدَّل.',
    'allergens.field.nameAr': 'الاسم بالعربية',
    'allergens.field.nameEn': 'الاسم بالإنكليزية',
    'allergens.field.kind': 'النوع',
    'allergens.field.icon': 'الرمز',
    'allergens.field.icon.hint': 'يظهر بجانب اسم الحساسية في كل التطبيقات. إن لم يناسب أيٌّ منها فاختر الرمز العام ⚠️.',
    'allergens.field.aliases': 'أسماء أخرى',
    'allergens.field.aliases.hint': 'كل اسم في سطر. هي الأسماء التي قد تُكتب بها الحساسية في سجل الطفل، مثل «طحينة» للسمسم.',
    'allergens.save': 'احفظ',
    'allergens.names': 'الأسماء التي تُعرف بها',
    'allergens.names.hint': 'يُطابَق اسم الحساسية في سجل الطفل بأيٍّ منها، دون اعتبار للتشكيل والهمزات والتاء المربوطة و«ال» وعبارة «حساسية من». لا يُحذف أحد الاسمين الأساسيين، بل يُغيَّر.',
    'allergens.names.remove': 'احذف «{name}»',
    'allergens.names.new': 'أسماء جديدة',
    'allergens.names.add': 'أضف الأسماء',
    'allergens.merge.title': 'دمج مادة أخرى في هذه',
    'allergens.merge.hint': 'إن كانت مادتان تعنيان الشيء نفسه: تنتقل كل أسماء المادة المختارة إلى «{name}»، فيظهر رمزها عند أطفالها، وتخرج المختارة من القائمة.',
    'allergens.merge.pick': 'المادة المدموجة',
    'allergens.merge.submit': 'ادمجها هنا',
    'allergens.retire': 'أخرجها من القائمة',
    'allergens.restore': 'أعدها إلى القائمة',
    'allergens.retire.hint': 'المادة الخارجة من القائمة لا تُقترح عند الكتابة، وتبقى أسماؤها تُطابَق، فلا يفقد أي طفل رمزها.',
    'allergens.pick': 'اختر مادة',
    'allergens.unmatched': 'أسماء لا تعرفها القائمة',
    'allergens.unmatched.lede': 'كتبتها الحضانات لأطفال مسجّلين ولم تطابق أي مادة، فتظهر عندهم برمز عام. يُعرض النص والأعداد فقط، دون اسم أي حضانة أو طفل. اختر الأسماء التي تعني الشيء نفسه، ثم أضفها إلى مادة موجودة أو اصنع منها مادة جديدة.',
    'allergens.unmatched.none': 'كل الأسماء المكتوبة معروفة.',
    'allergens.unmatched.choose': 'اختر «{name}»',
    'allergens.unmatched.column.name': 'الاسم',
    'allergens.unmatched.column.spellings': 'طرق كتابته',
    'allergens.unmatched.column.children': 'الأطفال',
    'allergens.unmatched.column.nurseries': 'الحضانات',
    'allergens.unmatched.chosen.one': 'اسم واحد مختار',
    'allergens.unmatched.chosen.two': 'اسمان مختاران',
    'allergens.unmatched.chosen.few': '{count} أسماء مختارة',
    'allergens.unmatched.chosen.many': '{count} اسمًا مختارًا',
    'allergens.unmatched.chosen.other': '{count} اسم مختار',
    'allergens.unmatched.addTo': 'أضفها إلى',
    'allergens.unmatched.add': 'أضف الأسماء',
    'allergens.unmatched.create': 'اصنع منها مادة جديدة',
    'backups.title': 'النسخ الاحتياطي',
    'backups.lede': 'نسخة كاملة من المنصّة: قاعدة البيانات وكل الملفات، مشفّرة. تُصنع نسخة كل ليلة، ويمكن صنع نسخة الآن، واختبار أي نسخة، واستعادة المنصّة منها.',
    'backups.create': 'اصنع نسخة الآن',
    'backups.list': 'النسخ المحفوظة',
    'backups.operations': 'العمليات',
    'backups.column.at': 'الوقت',
    'backups.column.source': 'المصدر',
    'backups.column.size': 'الحجم',
    'backups.column.contents': 'المحتوى',
    'backups.column.verified': 'الاختبار',
    'backups.megabytes': '{size} م.ب',
    'backups.contents': '{rows} صفًّا، {files} ملفًّا',
    'backups.source.nightly': 'ليلية',
    'backups.source.console': 'من وحدة التحكّم',
    'backups.source.pre-restore': 'قبل استعادة',
    'backups.notVerified': 'لم تُختبر',
    'backups.verified.passed': 'سليمة',
    'backups.verified.failed': 'غير سليمة',
    'backups.verify': 'اختبر',
    'backups.restore': 'استعد',
    'backups.nightly': 'المهمة الليلية',
    'backups.disabled.title': 'النسخ الاحتياطي غير مفعّل هنا',
    'backups.disabled.body': 'لم يُفعَّل النسخ الاحتياطي في هذه البيئة (Backup:Enabled)، أو لم يُضبط مفتاح تشفيره. اطلب من فريق التشغيل تفعيله.',
    'backups.running.Backup': 'تُصنع نسخة الآن. تظهر في القائمة حين تنتهي.',
    'backups.running.Verify': 'تُختبر نسخة الآن، في قاعدة بيانات تجريبية لا تمسّ البيانات الحيّة.',
    'backups.running.Restore': 'تجري استعادة. ستُغلق المنصّة للصيانة دقائق، ثم تحتاج إلى تسجيل الدخول من جديد.',
    'backups.operation.kind': 'العملية',
    'backups.operation.status': 'الحالة',
    'backups.operation.by': 'بطلب من',
    'backups.operation.message': 'التفاصيل',
    'backups.operation.reason': 'السبب: {reason}',
    'backups.kind.Backup': 'نسخ',
    'backups.kind.Verify': 'اختبار',
    'backups.kind.Restore': 'استعادة',
    'backups.status.Queued': 'في الانتظار',
    'backups.status.Running': 'جارية',
    'backups.status.Restarting': 'تُعاد تشغيل المنصّة',
    'backups.status.Succeeded': 'تمّت',
    'backups.status.Failed': 'فشلت',
    'backups.restore.title': 'استعادة نسخة {at}',
    'backups.restore.warning.title': 'تعيد الاستعادة بيانات كل الحضانات إلى لحظة النسخة',
    'backups.restore.warning.body': 'يضيع كل ما سُجّل بعدها، ومنه سجل التدقيق، إلا من نسخة الأمان التي تُصنع قبل الاستعادة. تُغلق المنصّة دقائق، وينتهي كل دخول قائم فيعيد الجميع تسجيل الدخول.',
    'backups.restore.confirm': 'للتأكيد، اكتب معرّف النسخة: {id}',
    'backups.restore.reason': 'سبب الاستعادة',
    'backups.restore.password': 'كلمة السر',
    'backups.restore.submit': 'استعد المنصّة من هذه النسخة',
    'backups.restore.started.title': 'بدأت الاستعادة',
    'backups.restore.started.body': 'ستُغلق المنصّة للصيانة خلال الاستعادة، ثم تعود على البيانات المستعادة. سجّل الدخول من جديد حين تعود.',

    'dashboard.title': 'لوحة المنصّة',
    'dashboard.lede': 'حالة المنصّة عبر جميع المشتركين.',
    'dashboard.activeChildren': 'الأطفال الفعّالون',
    'dashboard.activeStaff': 'الكادر الفعّال',
    'dashboard.newTenants': 'مشتركون جدد هذا الشهر',
    'dashboard.supportSessions': 'جلسات دعم نشطة',
    'dashboard.byStatus': 'المشتركون حسب الحالة',
    'dashboard.acrossPlatform': 'عبر كل المشتركين',
    'dashboard.trialsEnding': 'تجارب تحتاج قراراً',
    'dashboard.trialsEnding.hint': 'ما ينتهي قريباً، وما انتهى ولم يُفعَّل أو يُمدَّد بعد. الأقرب أولاً.',
    'dashboard.trialsEnding.empty': 'لا تجربة تنتهي قريباً.',
    'dashboard.trialsEnding.caption': 'التجارب التي تنتهي قريباً',
    'dashboard.trialsEnding.lastDay': 'آخر يوم',

    'tenants.title': 'المشتركون',
    'tenants.lede': 'كل حضانة أو روضة على المنصّة. لا تظهر هنا أي بيانات عن الأطفال.',
    'tenants.search': 'ابحث بالاسم أو المعرّف',
    'tenants.status.all': 'كل الحالات',
    'tenants.new': 'إضافة مشترك',
    'tenants.column.name': 'الاسم',
    'tenants.column.slug': 'المعرّف',
    'tenants.column.status': 'الحالة',
    'tenants.column.type': 'النوع',
    'tenants.column.children': 'الأطفال',
    'tenants.column.staff': 'الكادر',
    'tenants.column.classes': 'الصفوف',
    'tenants.column.created': 'تاريخ الإنشاء',
    'tenants.column.actions': 'إجراءات',
    'tenants.open': 'فتح',
    'tenants.empty': 'لا يوجد مشتركون مطابقون.',
    'tenants.caption': 'جدول المشتركين على المنصّة',

    'tenant.profile': 'الملف التشغيلي',
    'tenant.entryPath': 'مسار رابط الدخول:',
    'tenant.privacyNote':
      'يعرض هذا الملف أعداداً وبيانات تشغيلية فقط. لا يمكن الوصول من هنا إلى اسم طفل أو صورته أو تقريره أو رسائله.',
    'tenant.contact': 'جهة الاتصال',
    'tenant.timeZone': 'المنطقة الزمنية',
    'tenant.currency': 'العملة',
    'tenant.country': 'البلد',
    'tenant.type': 'النوع',
    'tenant.created': 'أُنشئ في',
    'tenant.suspend': 'إيقاف المشترك',
    'tenant.ownerInvitation': 'دعوة المالك',
    'tenant.ownerInvitation.hint': 'إن لم يقبل المالك دعوته بعد، أرسل إليه رابطاً جديداً برسالة نصية. يتوقف كل رابط سابق عن العمل.',
    'tenant.suspend.reason': 'سبب الإيقاف',
    'tenant.suspend.hint':
      'سيفقد كل مستخدمي هذا المشترك الوصول فوراً. يُسجَّل السبب في سجل التدقيق ويظهر لمالك الحضانة.',
    'tenant.suspend.confirm': 'تأكيد الإيقاف',
    'tenant.reactivate': 'إعادة التفعيل',
    'tenant.reactivate.hint': 'إن كان آخر يوم في التجربة لم يأتِ بعد، يعود المشترك إلى التجربة؛ وإلا يعود فعّالاً.',

    'tenant.address': 'العنوان',
    'tenant.subscription': 'الاشتراك',
    'modules.title': 'الخدمات',
    'modules.hint': 'تُفعَّل لكل مشترك على حدة. إيقاف خدمة يُبقي بياناتها للقراءة فقط.',
    'modules.notBuilt': 'لم تصدر بعد',
    'modules.off': 'موقوفة، وبياناتها للقراءة فقط',
    'modules.kind.Development': 'المهارات والسلوك',
    'modules.kind.Timetable': 'البرنامج الأسبوعي',
    'modules.kind.Transport': 'النقل',
    'modules.kind.Goods': 'المستلزمات المسلّمة للطفل',
    'modules.kind.Results': 'نتائج الاختبارات',
    'tenant.plan': 'الخطة',
    'tenant.plan.none': 'لا خطط بعد',
    'tenant.trial.lastDay': 'آخر يوم في التجربة',
    'tenant.trial.left.zero': 'تنتهي اليوم',
    'tenant.trial.left.one': 'بقي يوم واحد',
    'tenant.trial.left.two': 'بقي يومان',
    'tenant.trial.left.few': 'بقيت {count} أيام',
    'tenant.trial.left.many': 'بقي {count} يوماً',
    'tenant.trial.left.other': 'بقي {count} يوم',
    'tenant.trial.over': 'انتهت ولم يُقرَّر بعد',
    'tenant.trial.label': '{date} · {left}',
    'tenant.owner': 'المالك',
    'tenant.owner.none': 'لم يُدعَ مالك بعد.',
    'tenant.lastActivity': 'آخر نشاط',
    'tenant.lastActivity.none': 'لا نشاط بعد',
    'tenant.storage': 'المساحة المستخدمة',
    'tenant.storage.value': '{size} م.ب، قيست في {date}',
    'tenant.storage.none': 'لم تُقَس بعد. تُقاس مرة كل ليلة.',
    'tenant.activate': 'تفعيل الاشتراك',
    'tenant.activate.hint': 'تنتهي التجربة اليوم ويصبح المشترك فعّالاً. لا يُرسَل بعدها تذكير بانتهاء التجربة.',
    'tenant.activate.confirm': 'تأكيد التفعيل',
    'tenant.extend': 'تمديد التجربة',
    'tenant.extend.endsOn': 'آخر يوم جديد للتجربة',
    'tenant.extend.hint': 'بتوقيت الحضانة، ويوماً بعد اليوم على الأقل. يُرسَل تذكير انتهاء التجربة من جديد قبل هذا اليوم.',
    'tenant.extend.required': 'اختر اليوم الجديد.',
    'tenant.edit': 'تعديل بيانات المشترك',
    'tenant.edit.saved': 'حُفظت بيانات المشترك.',
    'tenant.edit.slugChanges': 'كل رابط أُرسل بالمعرّف «{slug}» يتوقف عن العمل: الدعوات المفتوحة وصفحة الدخول التي حفظها المستخدمون.',
    'tenant.edit.zoneChanges': 'كل وقت مخزَّن يظهر بتوقيت {zone} بدل {current}.',
    'tenant.edit.confirm': 'أفهم هذا الأثر وأريد الحفظ',

    'tenants.new.title': 'إضافة مشترك جديد',
    'tenants.new.lede': 'تُنشأ الحضانة وتُرسَل دعوة إلى المالك. لا تُعيَّن كلمة سر نيابةً عنه.',
    'tenants.new.nameAr': 'الاسم بالعربية',
    'tenants.new.nameEn': 'الاسم باللاتينية',
    'tenants.new.slug': 'المعرّف',
    'tenants.new.slug.hint': 'أحرف لاتينية صغيرة وأرقام وشرطات فقط. سيصبح رابط الدخول: guardian.daghfal.org/{slug}',
    'tenants.new.type': 'النوع',
    'tenants.new.type.Nursery': 'حضانة',
    'tenants.new.type.Kindergarten': 'روضة',
    'tenants.new.type.Both': 'حضانة وروضة',
    'tenants.new.country': 'البلد',
    'tenants.new.timeZone': 'المنطقة الزمنية',
    'tenants.new.locale': 'لغة الواجهة الافتراضية',
    'tenants.new.currency': 'العملة',
    'tenants.new.contactPhone': 'هاتف الحضانة',
    'tenants.new.contactEmail': 'بريد الحضانة',
    'tenants.new.owner': 'المالك',
    'tenants.new.ownerName': 'اسم المالك',
    'tenants.new.ownerPhone': 'هاتف المالك',
    'tenants.new.ownerPhone.invalid': 'أدخل رقماً صالحاً بالصيغة الدولية.',
    'tenants.new.submit': 'إنشاء وإرسال الدعوة',
    'tenants.new.created': 'أُنشئ المشترك «{name}».',
    'tenants.new.goToTenant': 'فتح ملف المشترك',
    'tenants.new.logo.hint': 'اختياري. يظهر في شاشة تسجيل الدخول، ويستطيع مديرو الحضانة تغييره لاحقاً.',
    'tenants.new.logoFailed': 'أُنشئ المشترك، لكن تعذّر رفع الشعار. أضفه من بطاقة «شعار الحضانة» في ملف المشترك.',
    'tenants.new.initialStatus': 'يبدأ',
    'tenants.new.initialStatus.Trial': 'بفترة تجربة',
    'tenants.new.initialStatus.Active': 'مشتركاً فعّالاً من اليوم',
    'tenants.new.initialStatus.hint': 'اختر «فعّالاً» لحضانة تدفع من البداية: لا فترة تجربة لها ولا تذكيرات بانتهائها.',

    'users.title': 'مستخدمو المنصّة',
    'users.lede': 'موظّفو Nasca-Sy. عضويّة المنصّة لا تمنح وصولاً إلى بيانات أي مشترك.',
    'users.invite': 'دعوة موظّف',
    'users.invite.hint': 'يعيّن المدعو كلمة سرّه بنفسه؛ المنصّة لا تعرف كلمة سرّ أحد ولا تعيّنها.',
    'users.invite.submit': 'إرسال الدعوة',
    'users.inviteAnother': 'دعوة موظّف آخر',
    'users.search': 'بحث بالاسم أو الرقم',
    'users.caption': 'مستخدمو المنصّة',
    'users.empty': 'لا مستخدمين مطابقين',
    'users.unnamed': 'بلا اسم',
    'users.you': 'أنت',
    'users.done': 'تم',
    'users.enable': 'إعادة التفعيل',
    'users.disable': 'إيقاف الوصول',
    'users.neverSignedIn': 'لم يسجّل الدخول بعد',
    'users.status.all': 'كل الحالات',
    'users.column.name': 'الاسم',
    'users.column.status': 'الحالة',
    'users.column.role': 'الدور',
    'users.column.lastLogin': 'آخر دخول',
    'users.column.actions': 'إجراءات',
    'users.role.choose': 'اختر الدور',
    'users.phone.invalid': 'أدخل رقماً بصيغة دولية صحيحة.',
    'users.invited.title': 'تمّت الدعوة',

    'announcements.title': 'إعلانات المنصّة',
    'announcements.lede': 'رسائل من Nasca-Sy إلى مالكي ومديري المشتركين. لا تصل إلى أولياء الأمور ولا إلى الكادر.',
    'announcements.compose': 'إعلان جديد',
    'announcements.compose.hint': 'يُحفظ كمسودّة أولاً. لا يُرسل شيء قبل أن تؤكّد الإرسال.',
    'announcements.empty': 'لا إعلانات بعد',
    'announcements.empty.body': 'أنشئ مسودّة، راجع عدد من ستصلهم، ثم أرسلها.',
    'announcements.field.title': 'العنوان',
    'announcements.field.body': 'النص',
    'announcements.audience.label': 'الجمهور',
    'announcements.audience.hint': 'المشتركون المؤرشفون مستثنون دائماً؛ لم يعودوا عملاء.',
    'announcements.audience.all': 'كل المشتركين',
    'announcements.audience.byStatus': 'حسب حالة الاشتراك',
    'announcements.audience.someTenants': '{count} مشترك محدّد',
    'announcements.saveDraft': 'حفظ كمسودّة',
    'announcements.send': 'إرسال',
    'announcements.send.confirm': 'تأكيد الإرسال',
    'announcements.discard': 'حذف المسودّة',
    'announcements.confirm.title': 'تأكيد الإرسال',
    'announcements.confirm.counting': 'جارٍ حساب عدد المستلمين…',
    'announcements.confirm.reach': 'سيصل إلى {people} شخصاً لدى {tenants} مشتركاً. لا يمكن التراجع بعد الإرسال.',
    'announcements.sent.title': 'أُرسل الإعلان',
    'announcements.sent.summary': 'وصل إلى {people} شخصاً لدى {tenants} مشتركاً.',
    'announcements.sentAt': 'أُرسل في {date} إلى {people} شخصاً.',
    'announcements.status.sent': 'مُرسل',

    'audit.title': 'سجل تدقيق المنصّة',
    'audit.lede': 'كل تغيير على المنصّة: من قام به، على أي نوع من السجلات، لدى أي مشترك، ومتى.',
    'audit.caption': 'سجل التدقيق',
    'audit.empty': 'لا سجلات مطابقة',
    'audit.empty.body': 'وسّع نطاق التاريخ أو أزل بعض عوامل التصفية.',
    'audit.privacy.title': 'ما لا يظهر هنا',
    'audit.privacy.body':
      'القيم السابقة للسجلات غير معروضة في وحدة تحكّم المنصّة، لأنها تتضمّن أسماء الأطفال. '
      + 'الاطّلاع عليها يتطلّب جلسة دعم يوافق عليها المشترك نفسه.',
    'audit.platformScope': 'على مستوى المنصّة',
    'audit.system': 'النظام',
    'audit.supportSession': 'جلسة دعم',
    'audit.column.at': 'التوقيت',
    'audit.column.tenant': 'المشترك',
    'audit.column.actor': 'الفاعل',
    'audit.column.action': 'العملية',
    'audit.column.entity': 'نوع السجل',
    'audit.column.endpoint': 'المصدر',
    'audit.action.Insert': 'إضافة',
    'audit.action.Update': 'تعديل',
    'audit.action.Delete': 'حذف',
    'audit.filter.allTenants': 'كل المشتركين',
    'audit.filter.allEntities': 'كل الأنواع',
    'audit.filter.allActions': 'كل العمليات',
    'audit.filter.from': 'من',
    'audit.filter.to': 'إلى',
    'audit.filter.supportOnly': 'جلسات الدعم فقط',
  },

  en: {
    'console.name': 'Platform console',
    'console.tagline': 'Tenant administration — Nasca-Sy',

    'nav.dashboard': 'Dashboard',
    'nav.tenants': 'Tenants',
    'nav.users': 'Platform users',
    'nav.accounts': 'Accounts',

    'accounts.title': 'Accounts that need a decision',
    'accounts.lede': 'Accounts no single nursery may decide for: someone at several nurseries or none, a nursery\'s administrator, and the platform team itself.',
    'accounts.queue.title': 'Password resets waiting',
    'accounts.queue.hint': 'Confirm who the person is before approving, through their nursery or a channel you trust.',
    'accounts.queue.empty': 'No requests are waiting.',
    'accounts.queue.approve': 'Confirmed',
    'accounts.queue.reject': 'Refuse',
    'accounts.name': 'Name',
    'accounts.phone': 'Phone',
    'accounts.requestedAt': 'Asked on',
    'accounts.from': 'From',
    'accounts.find.title': 'Find an account by its number',
    'accounts.find.submit': 'Find',
    'accounts.find.none': 'No account has this number.',
    'accounts.platform': 'The platform',
    'accounts.lastSeen': 'Last used: {date}',
    'accounts.status.Active': 'Active',
    'accounts.status.Suspended': 'Suspended',
    'accounts.status.Deactivated': 'Deactivated',
    'accounts.status.Released': 'Number released',
    'accounts.tier.None': 'Nothing live',
    'accounts.tier.Guardian': 'Guardian',
    'accounts.tier.Staff': 'Nursery staff',
    'accounts.tier.CentreAdmin': 'Nursery administrator',
    'accounts.tier.Platform': 'Platform team',
    'accounts.renumber.title': 'Move the account to a new number',
    'accounts.renumber.phone': 'New number',
    'accounts.renumber.submit': 'Send the link',
    'accounts.renumber.sent': 'The link went to {phone}. It expires at {date}.',
    'accounts.link.title': 'One-time recovery link',
    'accounts.link.hint': 'The account\'s owner sets a new password with it themselves. It is never texted: hand it over by a channel you trust.',
    'accounts.link.submit': 'Create the link',
    'accounts.link.shown': 'The link is shown here once only, and expires at {date}. Copy it now.',
    'accounts.release.title': 'Take the number off the account',
    'accounts.release.hint': 'When the number now belongs to someone else. The account keeps its name and records, and nobody can sign in to it from now on.',
    'accounts.release.confirm': 'I confirmed the number no longer belongs to the account\'s owner',
    'accounts.release.submit': 'Take the number off',
    'accounts.release.done': 'The number was taken off the account.',

    'passkeyRequired.title': 'Add a passkey',
    'passkeyRequired.body': 'The platform console opens only in a session begun with a passkey. Add one on this device, then sign out and sign in with it.',
    'passkeyRequired.signOut': 'Sign out to sign in with the passkey',
    'nav.announcements': 'Announcements',
    'nav.audit': 'Audit log',
    'nav.backups': 'Backups',
    'nav.allergens': 'Allergens',
    'allergens.title': 'Allergens',
    'allergens.lede': 'The list allergies are recognised by in every nursery, and each allergen’s picture. An allergy keeps the words the nursery wrote and is matched to this list whenever it is shown, so a name added here shows the picture at once for every child whose allergy is written that way, in every nursery.',
    'allergens.new': 'New allergen',
    'allergens.catalogue': 'The list',
    'allergens.search': 'Search by any name',
    'allergens.column.icon': 'Picture',
    'allergens.column.nameAr': 'Arabic name',
    'allergens.column.nameEn': 'English name',
    'allergens.column.kind': 'Kind',
    'allergens.column.names': 'Names',
    'allergens.column.state': 'State',
    'allergens.kind.Food': 'Food',
    'allergens.kind.Condition': 'Condition',
    'allergens.kind.Medicine': 'Medicine',
    'allergens.kind.Environment': 'Environment',
    'allergens.state.active': 'In the list',
    'allergens.state.retired': 'Retired',
    'allergens.state.merged': 'Merged',
    'allergens.edit': 'Edit',
    'allergens.close': 'Close',
    'allergens.editor.new': 'New allergen',
    'allergens.editor.edit': 'Edit “{name}”',
    'allergens.merged.notice': 'This allergen was merged into another, which took its names. It can no longer be changed.',
    'allergens.field.nameAr': 'Arabic name',
    'allergens.field.nameEn': 'English name',
    'allergens.field.kind': 'Kind',
    'allergens.field.icon': 'Picture',
    'allergens.field.icon.hint': 'Shown beside the allergy in every app. Where none fits, choose the generic ⚠️.',
    'allergens.field.aliases': 'Other names',
    'allergens.field.aliases.hint': 'One per line: the names an allergy may be written with in a child’s record, such as “tahini” for sesame.',
    'allergens.save': 'Save',
    'allergens.names': 'Names it is recognised by',
    'allergens.names.hint': 'An allergy in a child’s record matches any of these, whatever its diacritics, hamza, ة, “ال” or a leading “allergy to”. One of the two main names cannot be removed, only changed.',
    'allergens.names.remove': 'Remove “{name}”',
    'allergens.names.new': 'New names',
    'allergens.names.add': 'Add the names',
    'allergens.merge.title': 'Merge another allergen into this one',
    'allergens.merge.hint': 'Where two allergens mean the same thing: every name of the one chosen moves to “{name}”, its children show this picture, and the one chosen leaves the list.',
    'allergens.merge.pick': 'Allergen to merge in',
    'allergens.merge.submit': 'Merge it here',
    'allergens.retire': 'Retire',
    'allergens.restore': 'Put back in the list',
    'allergens.retire.hint': 'A retired allergen is no longer suggested, and its names still match, so no child loses its picture.',
    'allergens.pick': 'Choose an allergen',
    'allergens.unmatched': 'Names the list does not know',
    'allergens.unmatched.lede': 'Written by nurseries for enrolled children and matching no allergen, so they show the generic picture. The text and the counts only, never a nursery or a child. Choose the names that mean the same thing, then add them to an allergen or make a new one of them.',
    'allergens.unmatched.none': 'Every name written is known.',
    'allergens.unmatched.choose': 'Choose “{name}”',
    'allergens.unmatched.column.name': 'Name',
    'allergens.unmatched.column.spellings': 'Spellings',
    'allergens.unmatched.column.children': 'Children',
    'allergens.unmatched.column.nurseries': 'Nurseries',
    'allergens.unmatched.chosen.one': '1 name chosen',
    'allergens.unmatched.chosen.other': '{count} names chosen',
    'allergens.unmatched.addTo': 'Add them to',
    'allergens.unmatched.add': 'Add the names',
    'allergens.unmatched.create': 'Make a new allergen of them',
    'backups.title': 'Backups',
    'backups.lede': 'A whole copy of the platform: the database and every stored file, encrypted. One is made every night; you can make one now, test any of them, and restore the platform from one.',
    'backups.create': 'Back up now',
    'backups.list': 'Backups kept',
    'backups.operations': 'Operations',
    'backups.column.at': 'When',
    'backups.column.source': 'Made by',
    'backups.column.size': 'Size',
    'backups.column.contents': 'Contents',
    'backups.column.verified': 'Test',
    'backups.megabytes': '{size} MB',
    'backups.contents': '{rows} rows, {files} files',
    'backups.source.nightly': 'Nightly',
    'backups.source.console': 'Console',
    'backups.source.pre-restore': 'Before a restore',
    'backups.notVerified': 'Not tested',
    'backups.verified.passed': 'Sound',
    'backups.verified.failed': 'Not sound',
    'backups.verify': 'Test',
    'backups.restore': 'Restore',
    'backups.nightly': 'Nightly job',
    'backups.disabled.title': 'Backups are off here',
    'backups.disabled.body': 'Backups are not turned on in this environment (Backup:Enabled), or their key is not set. Ask the operations team to turn them on.',
    'backups.running.Backup': 'A backup is being made. It appears in the list when it is done.',
    'backups.running.Verify': 'A backup is being tested, in a throwaway database that does not touch live data.',
    'backups.running.Restore': 'A restore is running. The platform closes for maintenance for a few minutes; then everyone signs in again.',
    'backups.operation.kind': 'Operation',
    'backups.operation.status': 'Status',
    'backups.operation.by': 'Asked by',
    'backups.operation.message': 'Details',
    'backups.operation.reason': 'Reason: {reason}',
    'backups.kind.Backup': 'Backup',
    'backups.kind.Verify': 'Test',
    'backups.kind.Restore': 'Restore',
    'backups.status.Queued': 'Queued',
    'backups.status.Running': 'Running',
    'backups.status.Restarting': 'Restarting the platform',
    'backups.status.Succeeded': 'Done',
    'backups.status.Failed': 'Failed',
    'backups.restore.title': 'Restore the backup of {at}',
    'backups.restore.warning.title': 'A restore takes every nursery back to the backup’s moment',
    'backups.restore.warning.body': 'Everything recorded after it is lost, the audit trail included, except from the safety backup made first. The platform closes for a few minutes, and every session ends, so everyone signs in again.',
    'backups.restore.confirm': 'To confirm, type the backup’s id: {id}',
    'backups.restore.reason': 'Why it is being restored',
    'backups.restore.password': 'Password',
    'backups.restore.submit': 'Restore the platform from this backup',
    'backups.restore.started.title': 'The restore has started',
    'backups.restore.started.body': 'The platform is closed for maintenance during the restore, then comes back on the restored data. Sign in again when it does.',

    'dashboard.title': 'Platform dashboard',
    'dashboard.lede': 'The state of the platform across every tenant.',
    'dashboard.activeChildren': 'Active children',
    'dashboard.activeStaff': 'Active staff',
    'dashboard.newTenants': 'New tenants this month',
    'dashboard.supportSessions': 'Active support sessions',
    'dashboard.byStatus': 'Tenants by status',
    'dashboard.acrossPlatform': 'Across all tenants',
    'dashboard.trialsEnding': 'Trials to decide',
    'dashboard.trialsEnding.hint': 'Those ending soon, and those over and not yet activated or extended. Nearest first.',
    'dashboard.trialsEnding.empty': 'No trial is ending soon.',
    'dashboard.trialsEnding.caption': 'Trials ending soon',
    'dashboard.trialsEnding.lastDay': 'Last day',

    'tenants.title': 'Tenants',
    'tenants.lede': 'Every nursery and kindergarten on the platform. No child data appears here.',
    'tenants.search': 'Search by name or identifier',
    'tenants.status.all': 'All statuses',
    'tenants.new': 'Add tenant',
    'tenants.column.name': 'Name',
    'tenants.column.slug': 'Identifier',
    'tenants.column.status': 'Status',
    'tenants.column.type': 'Type',
    'tenants.column.children': 'Children',
    'tenants.column.staff': 'Staff',
    'tenants.column.classes': 'Classes',
    'tenants.column.created': 'Created',
    'tenants.column.actions': 'Actions',
    'tenants.open': 'Open',
    'tenants.empty': 'No tenants match.',
    'tenants.caption': 'Platform tenants',

    'tenant.profile': 'Operational profile',
    'tenant.entryPath': 'Entry link path:',
    'tenant.privacyNote':
      'This profile shows counts and operational data only. No child’s name, photograph, report or message is reachable from here.',
    'tenant.contact': 'Contact',
    'tenant.timeZone': 'Time zone',
    'tenant.currency': 'Currency',
    'tenant.country': 'Country',
    'tenant.type': 'Type',
    'tenant.created': 'Created',
    'tenant.suspend': 'Suspend tenant',
    'tenant.ownerInvitation': 'Owner invitation',
    'tenant.ownerInvitation.hint': 'If the owner has not accepted yet, text them a new link. Every earlier link stops working.',
    'tenant.suspend.reason': 'Reason for suspension',
    'tenant.suspend.hint':
      'Every user at this tenant loses access immediately. The reason is written to the audit log and shown to the owner.',
    'tenant.suspend.confirm': 'Confirm suspension',
    'tenant.reactivate': 'Reactivate',
    'tenant.reactivate.hint': 'If the last day of the trial is still ahead, the tenant goes back to its trial; otherwise it comes back active.',

    'tenant.address': 'Address',
    'tenant.subscription': 'Subscription',
    'modules.title': 'Modules',
    'modules.hint': 'Switched on per tenant. Switching a module off keeps its data, read only.',
    'modules.notBuilt': 'Not released yet',
    'modules.off': 'Switched off; its data is read only',
    'modules.kind.Development': 'Skills and behaviour',
    'modules.kind.Timetable': 'Weekly timetable',
    'modules.kind.Transport': 'Transport',
    'modules.kind.Goods': 'Goods given to a child',
    'modules.kind.Results': 'Test results',
    'tenant.plan': 'Plan',
    'tenant.plan.none': 'No plans yet',
    'tenant.trial.lastDay': 'Last day of the trial',
    'tenant.trial.left.zero': 'Ends today',
    'tenant.trial.left.one': 'One day left',
    'tenant.trial.left.other': '{count} days left',
    'tenant.trial.over': 'Over, not decided yet',
    'tenant.trial.label': '{date} · {left}',
    'tenant.owner': 'Owner',
    'tenant.owner.none': 'No owner invited yet.',
    'tenant.lastActivity': 'Last activity',
    'tenant.lastActivity.none': 'No activity yet',
    'tenant.storage': 'Storage used',
    'tenant.storage.value': '{size} MB, measured on {date}',
    'tenant.storage.none': 'Not measured yet. It is measured every night.',
    'tenant.activate': 'Activate',
    'tenant.activate.hint': 'The trial ends today and the tenant becomes active. No trial reminder is sent after this.',
    'tenant.activate.confirm': 'Confirm activation',
    'tenant.extend': 'Extend the trial',
    'tenant.extend.endsOn': 'New last day of the trial',
    'tenant.extend.hint': "In the nursery's timezone, and at least tomorrow. The trial reminder is sent again before this day.",
    'tenant.extend.required': 'Choose the new day.',
    'tenant.edit': 'Edit tenant details',
    'tenant.edit.saved': 'The tenant details are saved.',
    'tenant.edit.slugChanges': 'Every link sent with the identifier “{slug}” stops working: open invitations and the sign-in page people saved.',
    'tenant.edit.zoneChanges': 'Every stored time shows in {zone} instead of {current}.',
    'tenant.edit.confirm': 'I understand and want to save',

    'tenants.new.title': 'Add a tenant',
    'tenants.new.lede': 'The nursery is created and its owner invited. No password is set on their behalf.',
    'tenants.new.nameAr': 'Arabic name',
    'tenants.new.nameEn': 'Latin-script name',
    'tenants.new.slug': 'Identifier',
    'tenants.new.slug.hint': 'Lower-case letters, digits and hyphens. The entry link becomes guardian.daghfal.org/{slug}',
    'tenants.new.type': 'Type',
    'tenants.new.type.Nursery': 'Nursery',
    'tenants.new.type.Kindergarten': 'Kindergarten',
    'tenants.new.type.Both': 'Nursery and kindergarten',
    'tenants.new.country': 'Country',
    'tenants.new.timeZone': 'Time zone',
    'tenants.new.locale': 'Default interface language',
    'tenants.new.currency': 'Currency',
    'tenants.new.contactPhone': 'Nursery phone',
    'tenants.new.contactEmail': 'Nursery email',
    'tenants.new.owner': 'Owner',
    'tenants.new.ownerName': 'Owner’s name',
    'tenants.new.ownerPhone': 'Owner’s phone',
    'tenants.new.ownerPhone.invalid': 'Enter a valid number in international form.',
    'tenants.new.submit': 'Create and invite',
    'tenants.new.created': 'Tenant “{name}” created.',
    'tenants.new.goToTenant': 'Open tenant profile',
    'tenants.new.logo.hint': "Optional. Shown on the sign-in screen; the nursery's administrators can change it later.",
    'tenants.new.logoFailed': 'The tenant was created, but the logo could not be uploaded. Add it from the Nursery logo card on the tenant profile.',
    'tenants.new.initialStatus': 'Starts',
    'tenants.new.initialStatus.Trial': 'On a trial',
    'tenants.new.initialStatus.Active': 'Active from today',
    'tenants.new.initialStatus.hint': 'Choose active for a nursery paying from the start: it has no trial and no reminders about one.',

    'users.title': 'Platform users',
    'users.lede': 'Nasca-Sy staff. A platform membership grants no access to any tenant’s data.',
    'users.invite': 'Invite a colleague',
    'users.invite.hint': 'They set their own password. The platform never sets or learns anyone’s password.',
    'users.invite.submit': 'Send invitation',
    'users.inviteAnother': 'Invite another',
    'users.search': 'Search by name or number',
    'users.caption': 'Platform users',
    'users.empty': 'No matching users',
    'users.unnamed': 'Unnamed',
    'users.you': 'You',
    'users.done': 'Done',
    'users.enable': 'Restore access',
    'users.disable': 'Withdraw access',
    'users.neverSignedIn': 'Never signed in',
    'users.status.all': 'All statuses',
    'users.column.name': 'Name',
    'users.column.status': 'Status',
    'users.column.role': 'Role',
    'users.column.lastLogin': 'Last sign-in',
    'users.column.actions': 'Actions',
    'users.role.choose': 'Choose a role',
    'users.phone.invalid': 'Enter a valid international number.',
    'users.invited.title': 'Invitation issued',

    'announcements.title': 'Platform announcements',
    'announcements.lede': 'Messages from Nasca-Sy to tenant owners and managers. Guardians and teaching staff never receive these.',
    'announcements.compose': 'New announcement',
    'announcements.compose.hint': 'Saved as a draft first. Nothing is delivered until you confirm sending.',
    'announcements.empty': 'No announcements yet',
    'announcements.empty.body': 'Draft one, check how far it reaches, then send it.',
    'announcements.field.title': 'Title',
    'announcements.field.body': 'Message',
    'announcements.audience.label': 'Audience',
    'announcements.audience.hint': 'Archived tenants are always excluded — they are no longer customers.',
    'announcements.audience.all': 'Every tenant',
    'announcements.audience.byStatus': 'By subscription status',
    'announcements.audience.someTenants': '{count} named tenants',
    'announcements.saveDraft': 'Save draft',
    'announcements.send': 'Send',
    'announcements.send.confirm': 'Confirm send',
    'announcements.discard': 'Discard draft',
    'announcements.confirm.title': 'Confirm send',
    'announcements.confirm.counting': 'Counting recipients…',
    'announcements.confirm.reach': 'This will reach {people} people across {tenants} tenants. Sending cannot be undone.',
    'announcements.sent.title': 'Announcement sent',
    'announcements.sent.summary': 'Reached {people} people across {tenants} tenants.',
    'announcements.sentAt': 'Sent {date} to {people} people.',
    'announcements.status.sent': 'Sent',

    'audit.title': 'Platform audit log',
    'audit.lede': 'Every change on the platform: who made it, to which kind of record, in which tenant, and when.',
    'audit.caption': 'Audit trail',
    'audit.empty': 'No matching entries',
    'audit.empty.body': 'Widen the date range or clear a filter.',
    'audit.privacy.title': 'What is not shown here',
    'audit.privacy.body':
      'The values a record held before a change are not shown in the platform console, because they carry '
      + 'children’s names. Reading those requires a support session the tenant itself approves.',
    'audit.platformScope': 'Platform scope',
    'audit.system': 'System',
    'audit.supportSession': 'Support session',
    'audit.column.at': 'When',
    'audit.column.tenant': 'Tenant',
    'audit.column.actor': 'Actor',
    'audit.column.action': 'Action',
    'audit.column.entity': 'Record type',
    'audit.column.endpoint': 'Source',
    'audit.action.Insert': 'Created',
    'audit.action.Update': 'Changed',
    'audit.action.Delete': 'Deleted',
    'audit.filter.allTenants': 'All tenants',
    'audit.filter.allEntities': 'All record types',
    'audit.filter.allActions': 'All actions',
    'audit.filter.from': 'From',
    'audit.filter.to': 'To',
    'audit.filter.supportOnly': 'Support sessions only',
  },
};
